import { lazy, Suspense, useEffect, useState } from "react";
import './App.css';

import "bootstrap/dist/css/bootstrap.min.css";
// `pi pi-*` icons are used by Maintenance, notifications and disputes, so the
// icon font must load globally rather than only with MyOrders/Profile.
import "primeicons/primeicons.css";
import './css/Style.css';
import './css/Responsive.css';


import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import Header from "./layout/Header";
import Footer from "./layout/Footer";
import Maintenance from "./pages/Maintenance";
import useMaintenanceMode from "./hooks/useMaintenanceMode";

import CookieConsentPopup from "./components/CookieConsentPopup";
import AnalyticsTracker from "./components/AnalyticsTracker";
import RouteSeo from "./components/RouteSeo";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";


import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"
import ScrollToTop from "./components/ScrollToTop";
import PageLoader from "./components/PageLoader";
import PromotionalPopup from "./components/PromotionalPopup";
import OrderStatusPopup from "./components/OrderStatusPopup";
import WhatsAppEnquiryButton from "./components/WhatsAppEnquiryButton";

const Home = lazy(() => import("./pages/Home"));
const Shop = lazy(() => import("./pages/Shop"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Shopdetails = lazy(() => import("./pages/Shopdetails"));
const Wedding = lazy(() => import("./pages/Wedding"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Signup = lazy(() => import("./pages/Signup"));
const Profile = lazy(() => import("./pages/Profile"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const OrderCancelled = lazy(() => import("./pages/OrderCancelled"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const OrderQuestionnaire = lazy(() => import("./pages/OrderQuestionnaire"));
const CustomerNotifications = lazy(() => import("./pages/CustomerNotifications"));
const MyDisputes = lazy(() => import("./pages/MyDisputes"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));

function StorefrontApp() {
  const [whatsappProduct, setWhatsappProduct] = useState(null);

  return (
    <>
      <RouteSeo />
      <AnalyticsTracker />
      <ScrollToTop />
      <AuthProvider allowedRole="customer">
        <Header />
        <OrderStatusPopup />
        <PromotionalPopup />
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/cart" element={
            <ProtectedRoute>
              <Cart />
            </ProtectedRoute>
          } />
          <Route path="/checkout" element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          } />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-success/:order_id"
            element={
              <ProtectedRoute>
                <OrderSuccess />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order-cancelled/:order_id"
            element={
              <ProtectedRoute>
                <OrderCancelled />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-return"
            element={
              <ProtectedRoute>
                <PaymentReturn />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <MyOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order-questionnaire/:public_id"
            element={
              <ProtectedRoute>
                <OrderQuestionnaire />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <CustomerNotifications />
              </ProtectedRoute>
            }
          />

          <Route
            path="/wishlist"
            element={
              <ProtectedRoute>
                <Wishlist />
              </ProtectedRoute>
            }
          />

          <Route
            path="/shop-details/:product_id"
            element={
              <Shopdetails onEnquiryProductChange={setWhatsappProduct} />
            }
          />
          <Route path="/shop-wedding" element={<Wedding />} />
          <Route path="/faqs" element={<FAQ />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/contact-us" element={<Contact />} />
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />
          <Route path="/reset-password" element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } />
          <Route path="/signup" element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          } />

          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />

          <Route
            path="/disputes"
            element={
              <ProtectedRoute>
                <MyDisputes />
              </ProtectedRoute>
            }
          />

          </Routes>
        </Suspense>


        <ToastContainer
          position="top-right"
          autoClose={3000}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
          theme="colored"
        />
        <CookieConsentPopup />
        <WhatsAppEnquiryButton product={whatsappProduct} />
        <Footer />
      </AuthProvider>
    </>
  );
}

function App() {
  return (
    <Router>
      <MaintenanceAwareStorefront />
    </Router>
  );
}

function MaintenanceAwareStorefront() {
  const { maintenance, initialLoading, checking, refresh } =
    useMaintenanceMode();
  const [storefrontMounted, setStorefrontMounted] = useState(false);

  useEffect(() => {
    if (!initialLoading && !maintenance.enabled) {
      setStorefrontMounted(true);
    }
  }, [initialLoading, maintenance.enabled]);

  if (initialLoading) {
    return <PageLoader text="Checking store availability..." />;
  }

  const shouldRenderStorefront = storefrontMounted || !maintenance.enabled;

  return (
    <>
      {shouldRenderStorefront && (
        <div
          className={
            maintenance.enabled ? "maintenance-paused-storefront" : undefined
          }
          aria-hidden={maintenance.enabled ? "true" : undefined}
        >
          <StorefrontApp />
        </div>
      )}

      {maintenance.enabled && (
        <Maintenance
          settings={maintenance}
          checking={checking}
          onRetry={refresh}
          preservingSession={storefrontMounted}
        />
      )}
    </>
  );
}

export default App;
